import { useCallback, useState } from 'react';
import type { DirectedCastRole } from 'fym-shared/src/music24/directedVideoApi';
import { directedActionError, type DirectedActionError } from '../directedErrors';
import { useMakeDirectedCastPictureMutation } from '../directedVideoApi';
import { useDirectedVideoContext } from '../DirectedVideoContext';

/** One "Make a picture" request of the dialog or of a failed card. */
export interface CastPictureRequest {
  role: DirectedCastRole;
  label: string;
  description: string;
  /** The failed pending picture that this request tries again ("Try again", "Change the words"). */
  replacesPictureId?: string;
}

/**
 * Starts a cast picture (`POST .../cast/picture`). The picture then shows as a card of the cast
 * (`castPicturesPending`) while the AI makes it (owner report 2026-10-20: the dialog closes when the
 * picture starts). `start` answers true when the server took the request.
 */
export function useCastPictureRun() {
  const { accountId, catalogItemUuid, video } = useDirectedVideoContext();
  const [makePicture, making] = useMakeDirectedCastPictureMutation();
  const [error, setError] = useState<DirectedActionError | null>(null);

  const start = useCallback(
    async (request: CastPictureRequest): Promise<boolean> => {
      setError(null);
      try {
        await makePicture({ accountId, catalogItemUuid, uuid: video.uuid, ...request }).unwrap();
        return true;
      } catch (caught) {
        console.error('Directed video: the cast picture was not started', caught);
        setError(directedActionError(caught));
        return false;
      }
    },
    [accountId, catalogItemUuid, makePicture, video.uuid]
  );

  const reset = useCallback(() => setError(null), []);

  return { error, start, reset, isSending: making.isLoading };
}
